import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 wiring.
 *
 * Activates automatically when `REACT_APP_GA4_MEASUREMENT_ID` is set
 * in `frontend/.env` (format: `G-XXXXXXXXXX`). Falls silent otherwise
 * — zero impact on bundle size, zero requests fired.
 *
 * Once active:
 *   - Loads gtag.js asynchronously
 *   - Configures GA4 with `anonymize_ip: true` (UK GDPR posture)
 *   - Fires a `page_view` on every React Router route change
 *
 * Custom events: import `trackEvent` from this file. Common conversions:
 *   trackEvent('bumper_offer_claim',  { value: 37.99, currency: 'GBP' })
 *   trackEvent('begin_checkout',      { value: 44.99, currency: 'GBP', items: 1 })
 *   trackEvent('purchase',            { transaction_id, value, currency: 'GBP' })
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

function loadGtag(id) {
  if (typeof window === 'undefined' || window.__ga4Loaded) return;
  window.__ga4Loaded = true;
  // Insert <script async src="https://www.googletagmanager.com/gtag/js?id=...">
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, {
    anonymize_ip: true,
    send_page_view: false, // we fire manually on each route change
  });
}

export function trackEvent(name, params = {}) {
  if (!GA4_ID || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', name, params);
  } catch (e) { /* no-op */ }
}

export default function Analytics() {
  const location = useLocation();
  useEffect(() => {
    if (!GA4_ID) return;
    loadGtag(GA4_ID);
  }, []);
  useEffect(() => {
    if (!GA4_ID || typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);
  return null;
}
